<template>
  <ProgramSummaryCardLayout
    :program="program"
    :preview="preview"
    :draft="draft"
    :stacked="stacked"
    :card-state="cardState"
    :overlay="showOverlay"
    :time-alert="timeAlert"
  >
    <template #metric>
      <SummaryMetricRows
        :rows="metricRows"
        :is-dollar-budget="isDollarBudget"
        :basis="basis"
        :metrics-missing="mappedMetricRows.showMissingMetricCopy"
      />
    </template>

    <template #actions>
      <Button
        :color="buttonColor || 'brandPrimary'"
        :style="buttonStyle"
        :outlined="state !== 'active'"
        @click="onOpenProgram"
      >
        {{
          $t(
            state === "completed" || state === "inactive"
              ? "programs.summaryCard.review"
              : "programs.summaryCard.openProgram"
          )
        }}
      </Button>
      <Button
        v-if="showSubmitClaimButton"
        :color="buttonColor || 'brandPrimary'"
        :style="buttonStyle"
        :outlined="state === 'active'"
        @click="onSubmitClaim"
      >
        {{ $t("programs.summaryCard.submitClaim") }}
      </Button>
    </template>
  </ProgramSummaryCardLayout>
</template>

<script>
import ProgramSummaryCardLayout from "./ProgramSummaryCardLayout.vue";
import SummaryMetricRows from "./SummaryMetricRows.vue";
import Button from "@/components/generic/Button.vue";
import moment from "moment";
import { i18n } from "@/plugins/i18n";
import { toParticipantMetricRows } from "./toParticipantMetricRows";
import { buttonStyle, isClaimWindowClosed, isInClaimWindow } from "./programCardState";
import { openClaimForm } from "@/utils/claimForm";

// A program with no summary metric card still shows earnings as a Big Number.
const DEFAULT_EARNINGS_ROWS = [
  {
    rowId: "default",
    sortOrder: 0,
    columns: [
      {
        columnId: "default",
        slotIndex: 0,
        componentType: "BIG_NUMBER",
        metricSource: "EARNINGS",
      },
    ],
  },
];

export default {
  name: "DdpSummaryCard",
  components: { ProgramSummaryCardLayout, SummaryMetricRows, Button },
  emits: ["open-program"],
  props: {
    program: { type: Object, required: true },
    earnings: { type: [String, Number], default: null },
    basis: { type: String, default: null },
    isDollarBudget: { type: Boolean, default: null },
    buttonColor: { type: String, default: null },
    buttonTextColor: { type: String, default: null },
    preview: { type: Boolean, default: false },
    draft: { type: Boolean, default: false },
    stacked: { type: Boolean, default: false },
  },
  computed: {
    state() {
      return this.program.programState;
    },
    isClaimProgram() {
      return this.program.subtype === "claim";
    },
    inClaimWindow() {
      return isInClaimWindow(this.program);
    },
    cardState() {
      return ["coming_soon", "completed", "inactive"].includes(this.state) ? this.state : undefined;
    },
    buttonStyle() {
      return buttonStyle(this.buttonTextColor);
    },
    timeAlert() {
      if (this.isClaimProgram && this.inClaimWindow) {
        return this.$t("programs.summaryCard.lastDayToSubmitClaims", {
          date: moment(this.program.claimsCloseDate).locale(i18n.locale).format("ll"),
        });
      } else if (this.isClaimProgram && isClaimWindowClosed(this.program)) {
        return this.$t("programs.summaryCard.claimsWindowClosed");
      }
      return null;
    },
    showSubmitClaimButton() {
      return this.isClaimProgram && this.inClaimWindow;
    },
    showOverlay() {
      return (this.state === "completed" && !this.inClaimWindow) || this.state === "inactive";
    },
    mappedMetricRows() {
      const rows = this.program.summaryMetricRows;
      const earnings = { accumulatedReward: this.earnings };
      if (!Array.isArray(rows) || !rows.length) {
        // An empty node, not null: with no card there is no metric data to wait for.
        return toParticipantMetricRows(DEFAULT_EARNINGS_ROWS, {}, earnings);
      }
      return toParticipantMetricRows(rows, this.program.summaryMetric ?? null, earnings);
    },
    metricRows() {
      return this.mappedMetricRows.rows;
    },
  },
  methods: {
    onOpenProgram() {
      if (this.preview) return;
      this.$emit("open-program", this.program);
    },
    onSubmitClaim() {
      if (this.preview) return;
      openClaimForm(this.$router, this.program.uuid);
    },
  },
};
</script>
